/**
 * useChatPersistence — wires the per-step chat thread to the v2
 * reviewer-chat backend endpoints. Replaces the in-memory-only
 * pattern that lived in the reducer alone.
 *
 * Behaviour:
 *   - On mount (and on `threadId + agentName` becoming known): fires
 *     `GET /workflow/{tid}/chat?agent={name}`, dispatches
 *     `actions.hydrateChat(stepId, messages)` so the reducer slot
 *     reflects the persisted thread (including the welcome message
 *     seeded at workflow-init).
 *   - Exposes `sendUser(text)`: dispatches `SEND_USER_MESSAGE` to the
 *     reducer immediately (optimistic UX) AND POSTs to backend in
 *     the background. POST failure logged silently — chat is
 *     best-effort.
 *   - Exposes `recvAgent(text)`: dispatches `RECEIVE_AGENT_REPLY` AND
 *     POSTs as `role: agent`. Used by the revising→draft useEffect.
 *
 * Ref-guarded against double-fetch when the consumer re-renders
 * mid-load. Hydrates again whenever `(threadId, agentName)` changes
 * to a fresh pair — e.g. when the user navigates to a different step.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getChatMessages, appendChatMessage } from '../../../api/workflow';

interface BackendMessage {
  id: string;
  role: 'user' | 'agent' | 'system';
  text: string;
  timestamp: string | Date;
}

interface ListChatResponse {
  thread_id: string;
  agent_name: string;
  messages?: BackendMessage[];
}

interface AppendChatResponse {
  message: BackendMessage;
}

interface ChatActionsLike {
  hydrateChat: (stepId: number, messages: any[]) => void;
  sendUserMessage: (stepId: number) => void;
  receiveAgentReply: (stepId: number, text: string) => void;
}

export interface UseChatPersistenceInput {
  stepId: number;
  threadId: string | null | undefined;
  agentName: string | null | undefined;
  actions: ChatActionsLike;
}

export interface ChatPersistenceState {
  hydrating: boolean;
  sendUser: (text: string) => Promise<void>;
  recvAgent: (text: string) => Promise<void>;
}

// Normalize a backend ChatMessage into the reducer's shape. The two
// shapes match by design; the only delta is `timestamp` typing
// (backend ships ISO string after JSON encode, reducer holds a
// string anyway). Defensive coercion below.
function normalize(msg: BackendMessage) {
  const ts = msg.timestamp instanceof Date ? msg.timestamp.toISOString() : String(msg.timestamp || '');
  return { id: msg.id, role: msg.role, text: msg.text, timestamp: ts };
}

export function useChatPersistence({
  stepId,
  threadId,
  agentName,
  actions,
}: UseChatPersistenceInput): ChatPersistenceState {
  const [hydrating, setHydrating] = useState<boolean>(false);
  const lastFetchKey = useRef<string | null>(null);

  // Stable refs so the callbacks don't re-create on every actions
  // identity flip.
  const actionsRef = useRef(actions);
  actionsRef.current = actions;

  // Story H3 — per-(thread, agent) POST queue. Backend stores chat
  // messages via `$push` on the `chat_messages.{agent}` array, so
  // arrival order on the server equals insertion order. Without
  // chaining, two appends fired within ~50ms race over the network
  // and a refresh re-orders them. The queue serialises POSTs through
  // a `.then()` chain so the user's click order is preserved on the
  // wire. `.catch(() => null)` on each prior link prevents one
  // failure from poisoning the chain — subsequent POSTs still fire
  // even if an earlier one rejected.
  const queuesRef = useRef<Map<string, Promise<unknown>>>(new Map());
  const enqueuePost = useCallback(
    (key: string, fn: () => Promise<unknown>): Promise<unknown> => {
      const prev = queuesRef.current.get(key) ?? Promise.resolve();
      const next = prev.catch(() => null).then(() => fn());
      queuesRef.current.set(key, next);
      return next;
    },
    [],
  );

  // Initial hydrate — fires once per (threadId, agentName, stepId)
  // tuple. Ref-guarded to skip double-fetch within a single tuple
  // window (React StrictMode + re-renders).
  useEffect(() => {
    if (!threadId || !agentName) return;
    const key = `${threadId}::${agentName}::${stepId}`;
    if (lastFetchKey.current === key) return;
    lastFetchKey.current = key;

    // Story H8 — pair the local `cancelled` flag with an
    // AbortController so a rapid step switch actually aborts the
    // in-flight HTTP request, not just suppresses the dispatch.
    // `getChatMessages` forwards `{ signal }` to the shared axios
    // client; an aborted request rejects with `CanceledError`.
    const controller = new AbortController();
    let cancelled = false;
    setHydrating(true);
    (async () => {
      try {
        const resp = (await getChatMessages(threadId, agentName, {
          signal: controller.signal,
        })) as unknown as ListChatResponse;
        if (cancelled) return;
        const messages = (resp?.messages ?? []).map(normalize);
        actionsRef.current.hydrateChat(stepId, messages);
      } catch (e: any) {
        // Aborts on unmount/step-switch are expected — don't log.
        if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
        // Best-effort fetch; chat is a soft UX surface. Log + leave
        // the local reducer messages alone so the user can still type.
        // eslint-disable-next-line no-console
        console.warn('[useChatPersistence] fetch failed', e);
      } finally {
        if (!cancelled) setHydrating(false);
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [stepId, threadId, agentName]);

  const sendUser = useCallback(
    async (text: string) => {
      // Dispatch first for instant local echo (reducer pulls text
      // from `draft` itself; the consumer is expected to have written
      // it via UPDATE_DRAFT already).
      actionsRef.current.sendUserMessage(stepId);
      if (!threadId || !agentName) return;
      const key = `${threadId}::${agentName}`;
      try {
        await enqueuePost(key, () =>
          appendChatMessage(threadId, { agentName, role: 'user', text }),
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[useChatPersistence] POST user failed', e);
      }
    },
    [stepId, threadId, agentName, enqueuePost],
  );

  const recvAgent = useCallback(
    async (text: string) => {
      actionsRef.current.receiveAgentReply(stepId, text);
      if (!threadId || !agentName) return;
      const key = `${threadId}::${agentName}`;
      try {
        await enqueuePost(key, () =>
          appendChatMessage(threadId, { agentName, role: 'agent', text }),
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[useChatPersistence] POST agent failed', e);
      }
    },
    [stepId, threadId, agentName, enqueuePost],
  );

  return { hydrating, sendUser, recvAgent };
}
